import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

type Primitive = string | number | boolean
type Interpreter<Out extends Primitive = number> = (v: unknown) => Out

interface FilterConfig<K extends string = string, ChildKey extends string = string, Out extends Primitive = number> {
    key: K
    dataKey: ChildKey // for included Array objects
    interpreter: Interpreter<Out>
}

export const BRANCH_FILTER_KEY = 'branches'
export const IS_SHIFTED_FILTER_KEY = 'is_shifted'
export const REGISTERED_FILTER_CONFIG = [
    { key: BRANCH_FILTER_KEY, interpreter: (v: unknown) => Number(v), dataKey: 'id' },
    { key: IS_SHIFTED_FILTER_KEY, interpreter: (v: unknown) => String(v), dataKey: '' }
] satisfies readonly FilterConfig[]

export const useFilters = () => {
    const [params, setParams] = useSearchParams()
    const [filters, setFilters] = useState<Record<string, string[]>>({ })
    const [ignoreParams, setIgnoreParams] = useState<Record<string, string[]>>({})

    const addFilter = useCallback(
        (key: string, value: string) => {
            let existsFilter = filters[key] ?? []
            if (existsFilter.includes(value)) {
                existsFilter = existsFilter.filter(e => e !== value)
            } else {
                existsFilter.push(value)
            }

            const ignores = ignoreParams[key] ?? []
            if (!ignores.includes(value)) setIgnoreParams({ ...ignoreParams, [key]: [...ignores, value] })

            if (existsFilter?.length > 0) {
                setFilters({ ...filters, [key]: existsFilter })
            } else {
                let newFilters = filters
                delete newFilters[key]
                setFilters({ ...newFilters })
            }
        },
        [filters, ignoreParams]
    )

    const applyFilters = useCallback(() => {
        setParams(p => {
            const newParams = new URLSearchParams(p)
            Object.entries(filters).forEach(([key, filterValues]) => {
                if (filterValues?.length === 0) newParams.delete(key)
                const exists = newParams.getAll(key)
                filterValues.forEach(value => {
                    if (!exists.includes(value)) newParams.append(key, value)
                })
                for (const ex of exists) {
                    if (!filterValues.includes(ex)) newParams.delete(key, ex)
                }
            })
            return newParams
        })
        setIgnoreParams({})
    }, [filters, params, setParams])

    useEffect(() => {
        const newFilters = filters
        let needUpdate = false
        for (const keyData of REGISTERED_FILTER_CONFIG) {
            const key = keyData?.key
            const existsParams = params.getAll(key)
            const ignores = ignoreParams[key] ?? []
            if (existsParams?.length === 0) continue

            let existsFilter = filters[key] ?? []
            for (const value of existsParams) {
                if (!existsFilter.includes(value) && !ignores.includes(value)) {
                    existsFilter.push(value)
                    needUpdate = true
                }
            }
            filters[key] = existsFilter
        }

        if (needUpdate) setFilters({ ...newFilters })
    }, [filters, params, ignoreParams, REGISTERED_FILTER_CONFIG])

    const appliedFilters = useMemo(() => {
        const applied: Record<string, string[]> = {}
        for (const keyData of REGISTERED_FILTER_CONFIG) {
            const key = keyData?.key
            const existsParams = params.getAll(key)
            if (existsParams?.length === 0) continue

            const exists = applied[key] ?? []
            for (const value of existsParams) {
                if (!applied[key]) applied[key] = []

                if (!exists.includes(value)) {
                    applied[key].push(value)
                }
            }
        }
        return applied
    }, [params])

    const filtersCount = useMemo(() => {
        let counter = 0
        if (!appliedFilters) return counter
        Object.values(appliedFilters).forEach(values => {
            counter += values.length
        })
        return counter
    }, [appliedFilters])

    const filterArray = useCallback(
        <T>(data: T[]): T[] => {
            let filtered = data
            for (const conf of REGISTERED_FILTER_CONFIG) {
                const raw = appliedFilters[conf.key]
                if (!raw || raw.length === 0) continue

                const allowed = new Set(raw.map(conf.interpreter))
                filtered = filtered.filter(obj => {
                    // @ts-expect-error
                    const container = obj[conf.key]
                    if (!Array.isArray(container)) return false
                    return container.some((child: any) => allowed.has(child?.[conf.dataKey]))
                })
            }
            return filtered
        },
        [appliedFilters]
    )

    return {
        filters,
        addFilter,
        applyFilters,
        appliedFilters,
        filtersCount,
        filterArray
    }
}
